import { Fragment, useMemo, useState, useCallback, type ReactNode } from 'react';
import Stepper from '../../components/Stepper';

export interface CoOccurrenceRow {
  id: string;                     // unique key
  type: string;                   // grouping label (IOC type, "Actor", "Family", "Family/Actor")
  value: string;                  // display value
  href?: string;                  // pivot href (react-router path)
  count: number;                  // co-occurrence count (used as strength proxy when no jaccard)
  jaccard?: number | null;        // 0..1 if known
  shared_docs?: number | null;
  total_docs?: number | null;
  shared_attribution?: string | null;
  last_seen?: string | null;
  formula?: string | null;        // "12 of 30 shared / 8 unique to peer"
  shared_doc_refs?: { cite_key: string; title?: string | null }[] | null;
  first_co_mention?: string | null;
  reporting_orgs?: string[] | null;
}

type SortKey = 'strength' | 'shared' | 'last';
type SortDir = 'asc' | 'desc';

interface Props {
  rows: CoOccurrenceRow[];
  groupBy?: boolean;
  emptyLabel?: string;
  typeFacetLabel?: string;
  graphHref?: string;     // "View as graph ↗" target
}

export default function CoOccurrenceTable({ rows, groupBy = true, emptyLabel = 'No co-occurrences.', typeFacetLabel = 'Type', graphHref }: Props) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [minCount, setMinCount] = useState<number>(1);
  const [sortKey, setSortKey] = useState<SortKey>('strength');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const types = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.type);
    return Array.from(set).sort();
  }, [rows]);

  const maxCount = useMemo(() => rows.reduce((m, r) => Math.max(m, r.count), 0), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      // Belt-and-suspenders against extractor fragments that slipped past
      // the backend gate (single chars, empty values).
      if (!r.value || r.value.length < 3) return false;
      if (typeFilter && r.type !== typeFilter) return false;
      if (r.count < minCount) return false;
      if (q && !r.value.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, typeFilter, minCount]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      let av = 0, bv = 0;
      switch (sortKey) {
        case 'strength': av = a.jaccard ?? a.count / Math.max(1, maxCount); bv = b.jaccard ?? b.count / Math.max(1, maxCount); break;
        case 'shared': av = a.shared_docs ?? a.count; bv = b.shared_docs ?? b.count; break;
        case 'last': av = Date.parse(a.last_seen || '') || 0; bv = Date.parse(b.last_seen || '') || 0; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir, maxCount]);

  const grouped = useMemo(() => {
    if (!groupBy) return [{ type: '', items: sorted }];
    const map = new Map<string, CoOccurrenceRow[]>();
    for (const r of sorted) {
      const arr = map.get(r.type);
      if (arr) arr.push(r); else map.set(r.type, [r]);
    }
    return Array.from(map.entries()).map(([type, items]) => ({ type, items }));
  }, [sorted, groupBy]);

  const handleSort = useCallback((k: SortKey) => {
    setSortDir((d) => (sortKey === k ? (d === 'asc' ? 'desc' : 'asc') : 'desc'));
    setSortKey((c) => (c === k ? c : k));
  }, [sortKey]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  if (rows.length === 0) {
    return <div className="entity-table-empty">{emptyLabel}</div>;
  }

  const sortIndicator = (k: SortKey): ReactNode =>
    sortKey === k ? <span className="entity-table-sort">{sortDir === 'asc' ? '▲' : '▼'}</span> : null;

  return (
    <div className="entity-table">
      <div className="entity-table-toolbar">
        <input
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="entity-table-search"
        />
        {types.length > 1 && (
          <select
            value={typeFilter || ''}
            onChange={(e) => setTypeFilter(e.target.value || null)}
            className="entity-table-facet"
          >
            <option value="">All {typeFacetLabel.toLowerCase()}s</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <label className="entity-table-facet entity-table-min">
          Min co-occur
          <Stepper
            value={minCount}
            onChange={(v) => setMinCount(Math.max(1, v))}
            min={1}
            max={9999}
            step={1}
            stacked
          />
        </label>
        {graphHref && <a href={graphHref} className="entity-table-export">View as graph ↗</a>}
      </div>

      <table className="entity-cooccur-table">
        <thead>
          <tr>
            <th className="entity-docs-col-toggle" />
            <th>Value</th>
            <th onClick={() => handleSort('strength')}>Strength {sortIndicator('strength')}</th>
            <th onClick={() => handleSort('shared')}>Shared {sortIndicator('shared')}</th>
            <th>Attribution</th>
            <th onClick={() => handleSort('last')}>Last seen {sortIndicator('last')}</th>
          </tr>
        </thead>
        <tbody>
          {grouped.map((g) => (
            <Fragment key={g.type || '_'}>
              {groupBy && g.type && (
                <tr className="entity-cooccur-group-row">
                  <td colSpan={6}>{g.type.replace(/_/g, ' ')} ({g.items.length})</td>
                </tr>
              )}
              {g.items.map((r) => {
                const isOpen = expanded.has(r.id);
                const strength = r.jaccard != null ? r.jaccard : (maxCount > 0 ? r.count / maxCount : 0);
                const pct = Math.round(strength * 100);
                return (
                  <Fragment key={r.id}>
                    <tr className={isOpen ? 'is-expanded' : ''}>
                      <td className="entity-docs-col-toggle">
                        <button className="entity-docs-toggle" onClick={() => toggleExpand(r.id)} aria-label={isOpen ? 'Collapse' : 'Expand'}>{isOpen ? '▾' : '▸'}</button>
                      </td>
                      <td>
                        {r.href ? <a href={r.href} className="ioc-detail-doc-title">{r.value}</a> : r.value}
                      </td>
                      <td>
                        <div className="entity-cooccur-strength">
                          <div className="entity-cooccur-strength-bar" style={{ width: `${pct}%` }} />
                          <span className="entity-cooccur-strength-text">{r.jaccard != null ? r.jaccard.toFixed(2) : `${r.count}`}</span>
                        </div>
                      </td>
                      <td>{r.shared_docs != null ? `${r.shared_docs}${r.total_docs ? ` / ${r.total_docs}` : ''}` : r.count}</td>
                      <td>{r.shared_attribution || ''}</td>
                      <td>{r.last_seen || ''}</td>
                    </tr>
                    {isOpen && (
                      <tr className="entity-docs-expand-row">
                        <td />
                        <td colSpan={5}>
                          {r.formula && <div className="entity-cooccur-formula">{r.formula}</div>}
                          {r.first_co_mention && <div>First co-mention: {r.first_co_mention}</div>}
                          {r.reporting_orgs && r.reporting_orgs.length > 0 && (
                            <div>Reporting: {r.reporting_orgs.join(', ')}</div>
                          )}
                          {r.shared_doc_refs && r.shared_doc_refs.length > 0 && (
                            <ul className="entity-cooccur-shared">
                              {r.shared_doc_refs.map((d) => (
                                <li key={d.cite_key}><a href={`/sources/${encodeURIComponent(d.cite_key)}`}>{d.title || d.cite_key}</a></li>
                              ))}
                            </ul>
                          )}
                          {r.href && (
                            <div className="entity-docs-actions">
                              <a href={r.href} className="ioc-detail-action-btn">Open</a>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
      <div className="entity-table-footer">
        <span>{sorted.length} result{sorted.length !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}
